import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import { useVolunteer } from '../../contexts/VolunteerContext';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';

const AVAILABILITY_OPTIONS = ['Weekdays', 'Weekends', 'Evenings'];
// Local date as YYYY-MM-DD (toISOString would give UTC's date).
const todayIso = new Date().toLocaleDateString('en-CA');

const VolunteersLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    status,
    profile,
    myShifts,
    loading,
    error,
    fetchStatus,
    fetchProfile,
    fetchMyShifts,
    apply,
  } = useVolunteer();

  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [availability, setAvailability] = useState<string[]>([]);
  const [experience, setExperience] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Fetch profile and upcoming shifts when approved
  useEffect(() => {
    if (status?.volunteerStatus === 'approved') {
      fetchProfile();
      fetchMyShifts(true);
    }
  }, [status?.volunteerStatus, fetchProfile, fetchMyShifts]);

  const handleAvailabilityChange = (option: string) => {
    setAvailability((prev) =>
      prev.includes(option)
        ? prev.filter((a) => a !== option)
        : [...prev, option]
    );
  };

  const handleSubmitApplication = async () => {
    if (availability.length === 0) {
      setSubmitError('Please select at least one availability option');
      return;
    }
    if (!dateOfBirth) {
      setSubmitError('Please enter your date of birth');
      return;
    }
    if (dateOfBirth > todayIso) {
      setSubmitError('Date of birth cannot be in the future');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await apply({
        availability: availability.join(', '),
        experience,
        dateOfBirth,
      });
      setShowSuccess(true);
      setShowApplicationForm(false);
    } catch (err: any) {
      setSubmitError(err.response?.data?.error || 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && !status) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !status) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const volunteerStatus = status?.volunteerStatus ?? null;

  // State 1: Not applied
  if (volunteerStatus === null) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Volunteer with Team Jak
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Make a difference in your community
        </Typography>

        {!showApplicationForm ? (
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
            <VolunteerActivismIcon sx={{ fontSize: 80, color: '#00b7ed', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Join Our Volunteer Team
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Team Jak volunteers help run events, support families, and build a stronger
              community. Whether you can spare a few hours a week or a few hours a month,
              your time makes a real difference.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              As a volunteer, you will be able to browse and sign up for shifts, track your
              hours, and build your skills profile.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<VolunteerActivismIcon />}
              onClick={() => setShowApplicationForm(true)}
              sx={{ backgroundColor: '#00b7ed', '&:hover': { backgroundColor: '#009fcc' } }}
            >
              Apply to Volunteer
            </Button>
          </Paper>
        ) : (
          <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto' }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Volunteer Application
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tell us a bit about yourself and when you are available.
            </Typography>

            {submitError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {submitError}
              </Alert>
            )}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Availability *
            </Typography>
            <FormGroup sx={{ mb: 3 }}>
              {AVAILABILITY_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={availability.includes(option)}
                      onChange={() => handleAvailabilityChange(option)}
                      disabled={isSubmitting}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>

            <TextField
              fullWidth
              required
              type="date"
              label="Date of birth"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={isSubmitting}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: todayIso }}
              helperText="Volunteers need to be 15 or over. The volunteer coordinator will be in touch if not."
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Tell us about any relevant experience"
              placeholder="Any previous volunteer work, skills, or interests that may be relevant..."
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              disabled={isSubmitting}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmitApplication}
                disabled={isSubmitting || availability.length === 0 || !dateOfBirth}
                sx={{ backgroundColor: '#00b7ed', '&:hover': { backgroundColor: '#009fcc' } }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setShowApplicationForm(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}

        <Snackbar
          open={showSuccess}
          autoHideDuration={4000}
          onClose={() => setShowSuccess(false)}
        >
          <Alert severity="success" sx={{ width: '100%' }}>
            Application submitted successfully! We will review it and get back to you.
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // State 2: Pending
  if (volunteerStatus === 'pending') {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Volunteer with Team Jak
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Make a difference in your community
        </Typography>

        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <HourglassEmptyIcon sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Application Under Review
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for applying to volunteer with Team Jak! Our team is currently
            reviewing your application. We will notify you once a decision has been made.
          </Typography>

          {status?.volunteerAppliedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Applied on: {formatDate(status.volunteerAppliedAt)}
            </Typography>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Your Application Details
            </Typography>
            {status?.volunteerAvailability && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Availability:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                  {status.volunteerAvailability.split(', ').map((item) => (
                    <Chip key={item} label={item} size="small" />
                  ))}
                </Box>
              </Box>
            )}
            {status?.volunteerExperience && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Experience:
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {status.volunteerExperience}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    );
  }

  // State 4: Rejected
  if (volunteerStatus === 'rejected') {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Volunteer with Team Jak
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Make a difference in your community
        </Typography>

        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <BlockIcon sx={{ fontSize: 80, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Application Not Approved
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Unfortunately, your volunteer application was not approved at this time.
            This does not mean you cannot volunteer in the future.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you have questions or would like to discuss this further, please reach out
            to the Team Jak team directly.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Revoked: was a volunteer, volunteering has been ended (distinct from a rejected application).
  if (volunteerStatus === 'revoked') {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
          Volunteer with Team Jak
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Make a difference in your community
        </Typography>

        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <BlockIcon sx={{ fontSize: 80, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Volunteering Has Ended
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your volunteer access has been removed, so you can no longer sign up for shifts.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you have questions or would like to volunteer again, please reach out to the
            Team Jak team directly.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // State 3: Approved - Volunteer Dashboard
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const upcomingShifts = myShifts.filter(
    (s) => s.status === 'confirmed' && s.shift && new Date(s.shift.date) >= todayStart
  );

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Volunteer Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Welcome back, {user?.firstName}!
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Welcome Card */}
      <Card sx={{ mb: 4, backgroundColor: '#00b7ed', color: 'white' }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VolunteerActivismIcon sx={{ fontSize: 48 }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Volunteer Since:{' '}
              {status?.volunteerSince
                ? formatDate(status.volunteerSince)
                : 'N/A'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {profile?.totalHours ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssignmentIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {profile?.totalShifts ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Shifts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <EventIcon sx={{ fontSize: 40, color: '#00b7ed', mb: 1 }} />
              <Typography variant="h4" fontWeight={700}>
                {profile?.upcomingShifts ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upcoming Shifts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Quick Actions
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<EventIcon />}
          onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
          sx={{ backgroundColor: '#00b7ed', '&:hover': { backgroundColor: '#009fcc' } }}
        >
          Browse Shifts
        </Button>
        <Button
          variant="outlined"
          startIcon={<AssignmentIcon />}
          onClick={() => navigate(ROUTES.VOLUNTEER_MY_SHIFTS)}
        >
          My Shifts
        </Button>
        <Button
          variant="outlined"
          startIcon={<PersonIcon />}
          onClick={() => navigate(ROUTES.VOLUNTEER_PROFILE)}
        >
          My Profile
        </Button>
      </Box>

      {/* Upcoming Shifts List */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Upcoming Shifts
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : upcomingShifts.length === 0 ? (
        <Alert severity="info">
          You have no upcoming shifts.{' '}
          <Button
            size="small"
            onClick={() => navigate(ROUTES.VOLUNTEER_SHIFTS)}
            sx={{ ml: 1 }}
          >
            Browse Available Shifts
          </Button>
        </Alert>
      ) : (
        <List sx={{ bgcolor: 'background.paper', borderRadius: 2 }}>
          {upcomingShifts.map((registration, index) => (
            <ListItem
              key={registration.id}
              sx={{
                borderBottom:
                  index < upcomingShifts.length - 1 ? '1px solid #eee' : 'none',
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
              onClick={() =>
                navigate(
                  ROUTES.VOLUNTEER_SHIFT_DETAIL.replace(':id', registration.shiftId)
                )
              }
            >
              <ListItemText
                primary={
                  <Typography fontWeight={500}>
                    {registration.shift?.title}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EventIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {registration.shift?.date
                          ? formatDate(registration.shift.date)
                          : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {registration.shift?.startTime} - {registration.shift?.endTime}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOnIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {registration.shift?.location}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
              {registration.role && (
                <Chip label={registration.role.name} size="small" sx={{ ml: 1 }} />
              )}
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default VolunteersLandingPage;
